import { AfterViewInit, Directive, inject, OnInit } from '@angular/core';
import { RequestStatusService } from '@df/api/request/request-status.service';
import { SessionStorageService } from '@df/browser/storage/session-storage.service';
import { ApplePay } from '@df/module-apple-pay/apple-pay.namespace';
import { BraintreeApplePayExpressComponentDef } from '@df/module-braintree/apple-pay/braintree-apple-pay-express.component.def';
import { Giftcards } from '@df/module-giftcards/giftcards';
import { Nexus } from '@tomandco/nexus-api-types';
import { StoreCredit } from '../../store-credit/store-credit.namespace';
import { StoreCreditService } from './../../store-credit/store-credit.service';

const TOTALS_REQUESTS = [
    'checkout/removeItem',
    'basket/add',
    'basket/qty',
    'basket/remove',
    'basket/changeRequest',
    'coupon/remove',
    'coupon/add',
    StoreCredit.Endpoints.Apply,
    StoreCredit.Endpoints.Remove,
    Giftcards.Endpoints.Add,
    Giftcards.Endpoints.Remove,
    Nexus.Api.Wishlist.MoveFromBasket.path
];

@Directive()
export abstract class LocalBraintreeApplePayExpressComponentDef
    extends BraintreeApplePayExpressComponentDef
    implements OnInit, AfterViewInit
{
    /**
     * Totals requests in progess model
     */
    private totalsUpdating = false;

    protected storeCredit = inject(StoreCreditService);
    protected requestStatusService = inject(RequestStatusService);
    protected sessionStorageService = inject(SessionStorageService);

    override ngOnInit(): void {
        super.ngOnInit();

        this.sessionStorageService.set('expressPayment', true);
    }

    override ngAfterViewInit() {
        super.ngAfterViewInit();
        this.subscriptions.add(this.requestStatusService.subject.subscribe(() => this.onRequestsChange()));
        this.onRequestsChange();
    }

    get displayLoader(): boolean {
        return !!(this.totalsUpdating && this.braintreeButtonInstance);
    }

    /**
     * Returns button css class
     * @returns {string}
     */
    protected override getButtonClass(): string {
        return 'apple-pay-button-white';
    }

    /**
     * Shipping address data to use for preselection
     * @returns {ApplePay.IContact}
     */
    protected override getShippingContact(): ApplePay.IContact {
        return {};
    }

    /**
     * Billing address data to use for preselection
     * @returns {ApplePay.IContact}
     */
    protected override getBillingContact(): ApplePay.IContact {
        return {};
    }

    /**
     * Validate checkout
     */
    protected override validateCheckout(): void {}

    /**
     * Returns totals lines (except grand total, it is providede in total)
     * @returns {ApplePay.ILineItem}
     */
    protected override getLineItems(): ApplePay.ILineItem[] {
        const lines: ApplePay.ILineItem[] = [
            this.getShippingLine(),
            this.getDiscountLine(),
            this.getCreditsLine(),
            this.getGiftCardsLine()
        ].filter(i => !!i);

        if (lines.length) {
            const subtotalLine = this.getSubtotalLine();
            if (subtotalLine) {
                lines.unshift(subtotalLine);
            }
        }

        return lines;
    }

    /**
     * Returns store credits line item
     * @returns {ApplePay.ILineItem}
     */
    protected getCreditsLine(): ApplePay.ILineItem | undefined {
        const value = this.getTotals()?.['credits'];
        if (value) {
            const label = this.storeCredit.welcomeOffer ? 'Welcome offer' : 'Store Credit';
            return this.getLineItem(label, value * -1);
        }
        return undefined;
    }

    protected getGiftCardsLine(): ApplePay.ILineItem | undefined {
        const value = this.getTotals()?.giftcards;
        if (value) {
            return this.getLineItem('Gift Cards', value * -1);
        }
        return undefined;
    }

    /**
     * On requests updata callback
     */
    protected onRequestsChange(): void {
        this.totalsUpdating = !!this.requestStatusService.getRequests().find(request => {
            const path = request.path;

            if (path) {
                return TOTALS_REQUESTS.indexOf(path) > -1;
            }

            return false;
        });

        this.detectChanges();
    }

    /**
     * Returns if click is allowed
     * @returns {boolean}
     */
    protected override allowClick(): boolean {
        return !this.totalsUpdating;
    }
}
